<template>
  <ClicColInterno
    :cp="cp"
    :key="componentKey"
    :id="componentKey"
  >
    <div class="form-group left-label">
      <div class="d-flex flex-row align-items-start">
          <label class="form-label form-label-clic" v-if="label">
            <b v-if="required && (value == undefined || value === '')" class="text-danger">*</b>
            {{ $storage.getTextoTraduzido(label) }}
          </label>
      </div>

      <div class="form-control-wrap">

        <!-- Modo dropzone (opt-in via prop mode = 'dropzone') -->
        <template v-if="mode == 'dropzone'">
          <div
            class="clic-upload-dropzone"
            :class="[this.class, {
              'clic-upload-dropzone--dragging' : dragging,
              'clic-upload-dropzone--disabled' : disabled || uploading,
              'clic-upload-dropzone--invalid'  : fieldState === false || !!erroArquivo,
              'clic-upload-dropzone--sm'       : size == 'sm',
              'clic-upload-dropzone--lg'       : size == 'lg',
            }]"
            :style="style"
            @click="abrirSeletor"
            @dragenter.prevent="onDragEnter"
            @dragover.prevent
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
          >
            <input
              ref="inputNativo"
              type="file"
              class="d-none"
              :accept="accept"
              :multiple="multiple"
              :capture="capture"
              :disabled="disabled || uploading"
              @change="onInputNativo"
            >

            <div class="clic-upload-dropzone__icon-box" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </div>

            <div class="clic-upload-dropzone__title">
              {{ dragging ? $storage.getTextoTraduzido(dropPlaceholder) : $storage.getTextoTraduzido(placeholder) }}
            </div>

            <div class="clic-upload-dropzone__subtitle" v-if="subtitle && !dragging">
              {{ $storage.getTextoTraduzido(subtitle) }}
            </div>

            <div class="clic-upload-dropzone__actions" v-show="!dragging">
              <button
                type="button"
                class="clic-upload-dropzone__btn"
                :disabled="disabled || uploading"
                @click.stop="abrirSeletor"
              >
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="12" y1="18" x2="12" y2="12"/>
                  <line x1="9" y1="15" x2="15" y2="15"/>
                </svg>
                {{ $storage.getTextoTraduzido(browseText || 'Escolher Arquivo') }}
              </button>
            </div>
          </div>

          <div class="clic-upload-dropzone__hint" v-if="hint || accept || maxSize">
            <template v-if="hint">{{ $storage.getTextoTraduzido(hint) }}</template>
            <template v-else>
              <span v-if="accept">{{ $storage.getTextoTraduzido('Formatos suportados') }}: {{ accept }}</span>
              <span v-if="accept && maxSize"> · </span>
              <span v-if="maxSize">{{ $storage.getTextoTraduzido('Tamanho máximo') }}: {{ formatarTamanho(obterMaxSizeBytes()) }}</span>
            </template>
          </div>

          <ul class="clic-upload-lista" v-if="getFiles().length">
            <li v-for="(arquivo, index) in getFiles()" :key="`${arquivo.name}-${arquivo.size}-${arquivo.lastModified}`">
              <span class="clic-upload-lista__nome" :title="arquivo.name">{{ arquivo.name }}</span>
              <span class="clic-upload-lista__tamanho">{{ formatarTamanho(arquivo.size) }}</span>
              <button
                type="button"
                class="close clic-upload-lista__remover"
                :disabled="disabled || uploading"
                :aria-label="`${$storage.getTextoTraduzido('Remover')} ${arquivo.name}`"
                @click.stop="removerArquivo(index)"
              >&times;</button>
            </li>
          </ul>

          <b-progress
            v-if="uploading"
            class="mt-2"
            :value="uploadProgress"
            :max="100"
            show-progress
            animated
          ></b-progress>
        </template>

        <!-- Modo padrão (comportamento original) -->
        <b-form-file
          v-else
          :style="style"
          :label="label"
          v-model="value"
          :invalidFeedbackMessage="invalidFeedbackMessage"
          :autofocus="autofocus"
          :browse-text="browseText"
          :accept="accept"
          :capture="capture"
          :disabled="disabled"
          :directory="directory"
          :drop-placeholder="dropPlaceholder"
          :file-name-formatter="fileNameFormatter"
          :multiple="multiple"
          :no-drop-placeholder="noDropPlaceholder"
          :no-traverse="noTraverse"
          :placeholder="$storage.getTextoTraduzido(placeholder)"
          :required="required"
          :plain="plain"
          :size="size"
          :state="fieldState"
          :class="`form-control ${this.class}`"
          @change="change"
          @input="input"
        >

        </b-form-file>

        <b-progress
          v-if="uploading && mode != 'dropzone'"
          class="mt-2"
          :value="uploadProgress"
          :max="100"
          show-progress
          animated
        ></b-progress>

        <div v-if="erroArquivo" class="text-danger small mt-1" role="alert">
          {{ erroArquivo }}
        </div>

        <b-form-invalid-feedback
          v-if="invalidFeedbackMessage"
          :class="{ 'd-block' : mode == 'dropzone' && fieldState === false }"
        >
          {{invalidFeedbackMessage}}
        </b-form-invalid-feedback>
      </div>
    </div>
  </ClicColInterno>
</template>

<script>

import ComponentMixin from '../../core/ComponentMixin';
import { BFormFile, BFormInvalidFeedback, BProgress } from 'bootstrap-vue';

const UNIDADES_TAMANHO = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3 };

export default {
  name: 'ClicInputFile',
  mixins: [ComponentMixin],
  components: { BFormFile, BFormInvalidFeedback, BProgress },

  data() {
    return {
      value           : undefined,
      oldValue        : undefined,
      fieldState      : undefined,
      dragging        : false,
      dragDepth       : 0,
      uploading       : false,
      uploadProgress  : 0,
      erroArquivo     : undefined,
      public:{
        isValid         : this.validateField,
        clearField      : this.clearField,
        undo            : this.undo,
        apply           : this.apply,
        isValueChanged  : this.isValueChanged,
        getFiles        : this.getFiles,
        getFormData     : this.getFormData,
        upload          : this.upload,
        readFile        : this.readFile,
      }
    }
  },

  // mounted() {
  //   this.oldValue = this.value;
  // },

  methods: {
    input(value) {
      // Arquivo recusado pelo maxSize: limpa a seleção sem disparar o evento do usuário
      if  (this.possuiValidacao() && this.lodash.castArray(value || []).length) {
        if  (!this.validarArquivos(this.lodash.castArray(value)).length) {
          this.$nextTick(() => { this.value = undefined; });
          return;
        }
      }

      if(this.lodash.has(this.cp, 'events.input')){
        this._execEvent('input', { value });
      }
    },
    change(value) {
      if  (this.possuiValidacao()) {
        const arquivos = Array.from(this.lodash.get(value, 'target.files') || this.lodash.get(value, 'dataTransfer.files') || []);
        if  (arquivos.length && !this.validarArquivos(arquivos, false).length) {
          return;
        }
      }

      if(this.lodash.has(this.cp, 'events.change')){
        this._execEvent('change', { value });
      }
    },

    // ---------- Validação (só atua quando maxSize está configurado, ou no modo dropzone) ----------

    possuiValidacao() {
      return !!this.obterMaxSizeBytes() || this.mode == 'dropzone';
    },

    obterMaxSizeBytes() {
      if  (this.maxSize === undefined || this.maxSize === null || this.maxSize === '') {
        return undefined;
      }
      if  (typeof this.maxSize == 'number') {
        return this.maxSize * UNIDADES_TAMANHO.MB;
      }
      const match = String(this.maxSize).trim().toUpperCase().match(/^([\d.,]+)\s*(B|KB|MB|GB)?$/);
      if  (!match) {
        console.error(`[ClicInputFile - ${this.lodash.get(this.cp, 'id')}] maxSize inválido: '${this.maxSize}'. Use número (MB) ou texto como '500KB', '10MB'.`);
        return undefined;
      }
      return parseFloat(match[1].replace(',', '.')) * UNIDADES_TAMANHO[match[2] || 'MB'];
    },

    arquivoAceito(arquivo) {
      if  (!this.accept) {
        return true;
      }
      const nome = (arquivo.name || '').toLowerCase();
      const tipo = (arquivo.type || '').toLowerCase();

      return String(this.accept).split(',').map(t => t.trim().toLowerCase()).filter(Boolean).some(regra => {
        if  (regra.startsWith('.')) { return nome.endsWith(regra); }
        if  (regra.endsWith('/*'))  { return tipo.startsWith(regra.slice(0, -1)); }
        return tipo == regra;
      });
    },

    // Retorna somente os arquivos válidos. Recusados disparam o evento 'invalid' e a mensagem inline.
    validarArquivos(arquivos, notificar = true) {
      const maxBytes  = this.obterMaxSizeBytes();
      const validos   = [];
      const recusados = [];

      for (const arquivo of arquivos) {
        if  (this.mode == 'dropzone' && !this.arquivoAceito(arquivo)) {
          recusados.push({ file: arquivo, motivo: 'accept' });
        } else if (maxBytes && arquivo.size > maxBytes) {
          recusados.push({ file: arquivo, motivo: 'maxSize' });
        } else {
          validos.push(arquivo);
        }
      }

      if  (notificar) {
        this.erroArquivo = undefined;
        if  (recusados.length) {
          const textoMotivo = (r) => r.motivo == 'maxSize'
            ? `${this.$storage.getTextoTraduzido('excede o tamanho máximo de')} ${this.formatarTamanho(maxBytes)}`
            : `${this.$storage.getTextoTraduzido('tipo não permitido')} (${this.accept})`;

          this.erroArquivo = recusados.map(r => `${r.file.name}: ${textoMotivo(r)}`).join(' | ');

          if  (this.lodash.has(this.cp, 'events.invalid')) {
            this._execEvent('invalid', { files: recusados.map(r => r.file), recusados });
          }
        }
      }

      return validos;
    },

    formatarTamanho(bytes) {
      if  (!bytes && bytes !== 0) { return ''; }
      if  (bytes < UNIDADES_TAMANHO.KB) { return `${bytes} B`; }
      if  (bytes < UNIDADES_TAMANHO.MB) { return `${(bytes / UNIDADES_TAMANHO.KB).toFixed(1)} KB`; }
      if  (bytes < UNIDADES_TAMANHO.GB) { return `${(bytes / UNIDADES_TAMANHO.MB).toFixed(1)} MB`; }
      return `${(bytes / UNIDADES_TAMANHO.GB).toFixed(1)} GB`;
    },

    // ---------- Dropzone ----------

    abrirSeletor() {
      if  (this.disabled || this.uploading) { return; }
      this.$refs.inputNativo && this.$refs.inputNativo.click();
    },

    onDragEnter() {
      if  (this.disabled || this.uploading) { return; }
      this.dragDepth++;
      this.dragging = true;
    },

    onDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if  (!this.dragDepth) { this.dragging = false; }
    },

    onDrop(event) {
      this.dragDepth = 0;
      this.dragging  = false;
      if  (this.disabled || this.uploading) { return; }
      this.receberArquivos(Array.from(this.lodash.get(event, 'dataTransfer.files') || []), event);
    },

    onInputNativo(event) {
      this.receberArquivos(Array.from(event.target.files || []), event);
      // Permite selecionar o mesmo arquivo novamente após remover
      event.target.value = '';
    },

    receberArquivos(arquivos, event) {
      if  (!arquivos.length) { return; }

      let validos = this.validarArquivos(arquivos);
      if  (!validos.length) { return; }

      let novoValor;
      if  (this.multiple) {
        // No modo múltiplo os arquivos são acumulados (sem duplicar)
        const chave  = (f) => `${f.name}|${f.size}|${f.lastModified}`;
        const atuais = this.getFiles();
        novoValor = [...atuais, ...validos.filter(v => !atuais.some(a => chave(a) == chave(v)))];
      } else {
        novoValor = validos[0];
      }

      this.value = novoValor;
      this.fieldState = undefined;

      if(this.lodash.has(this.cp, 'events.change')){
        this._execEvent('change', { value: event, files: validos });
      }
      if(this.lodash.has(this.cp, 'events.input')){
        this._execEvent('input', { value: novoValor });
      }
    },

    removerArquivo(index) {
      const arquivos = this.getFiles();
      arquivos.splice(index, 1);
      this.value = this.multiple ? (arquivos.length ? arquivos : undefined) : undefined;
      this.erroArquivo = undefined;

      if(this.lodash.has(this.cp, 'events.input')){
        this._execEvent('input', { value: this.value });
      }
    },

    // ---------- API pública ----------

    getFiles() {
      if  (!this.value) { return []; }
      return Array.isArray(this.value) ? [...this.value] : [this.value];
    },

    resolverValorCodigo(valor) {
      if  (typeof valor == 'string') {
        try { valor = eval(`(${valor})`); } catch (e) {
          console.error(`[ClicInputFile - ${this.lodash.get(this.cp, 'id')}] Falha ao avaliar configuração: ${e}`);
          return undefined;
        }
      }
      if  (typeof valor == 'function') {
        valor = valor.call(this);
      }
      return valor;
    },

    getFormData(extraData = {}) {
      const formData  = new FormData();
      const fieldName = this.uploadFieldName || 'file';
      const extras    = { ...(this.resolverValorCodigo(this.lodash.get(this.cp, 'props.uploadExtraData')) || {}), ...(extraData || {}) };

      this.getFiles().forEach(arquivo => formData.append(fieldName, arquivo, arquivo.name));

      Object.keys(extras).forEach(chave => {
        const valor = extras[chave];
        if  (valor === undefined || valor === null) { return; }
        formData.append(chave, (typeof valor == 'object' && !(valor instanceof Blob)) ? JSON.stringify(valor) : valor);
      });

      return formData;
    },

    async upload(extraData) {
      const config = this.resolverValorCodigo(this.lodash.get(this.cp, 'props.configAxiosUpload'));

      if  (!config || !config.url) {
        throw new Error(`[ClicInputFile - ${this.lodash.get(this.cp, 'id')}] configAxiosUpload não informado (é necessário ao menos { url }).`);
      }
      if  (!this.getFiles().length) {
        throw new Error(this.$storage.getTextoTraduzido('Selecione um arquivo'));
      }

      // O navegador define o Content-Type com o boundary do multipart
      const headers = { ...(config.headers || {}) };
      Object.keys(headers).filter(h => h.toLowerCase() == 'content-type').forEach(h => delete headers[h]);

      this.uploading      = true;
      this.uploadProgress = 0;

      try {
        const response = await this.$axios({
          method : 'POST',
          ...config,
          headers,
          data   : this.getFormData(extraData),
          onUploadProgress : (event) => {
            const percent = event.total ? Math.round((event.loaded * 100) / event.total) : 0;
            this.uploadProgress = percent;
            if  (this.lodash.has(this.cp, 'events.uploadProgress')) {
              this._execEvent('uploadProgress', { percent, event });
            }
          }
        });

        if  (this.lodash.has(this.cp, 'events.uploadSuccess')) {
          this._execEvent('uploadSuccess', { response });
        }
        return response;

      } catch (error) {
        if  (this.lodash.has(this.cp, 'events.uploadError')) {
          this._execEvent('uploadError', { error });
        }
        throw error;

      } finally {
        this.uploading = false;
      }
    },

    // tipo: 'text' | 'dataURL' | 'arrayBuffer' | 'binaryString'
    readFile(tipo = 'text', arquivo = this.getFiles()[0], encoding = 'UTF-8') {
      return new Promise((resolve, reject) => {
        if  (!arquivo) {
          return reject(new Error(this.$storage.getTextoTraduzido('Selecione um arquivo')));
        }
        const reader = new FileReader();
        reader.onload  = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);

        switch (tipo) {
          case 'dataURL'      : reader.readAsDataURL(arquivo); break;
          case 'arrayBuffer'  : reader.readAsArrayBuffer(arquivo); break;
          case 'binaryString' : reader.readAsBinaryString(arquivo); break;
          default             : reader.readAsText(arquivo, encoding);
        }
      });
    },
  },

};

</script>

<style>
.invalid-feedback{
  margin-top:0;
}
</style>

<style scoped>
.clic-upload-dropzone {
  --clic-upload-cor: var(--cor-principal, #f7931e);

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 28px 16px;
  border: 2px dashed var(--clic-upload-cor);
  border-radius: 12px;
  background: transparent;
  text-align: center;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}

.clic-upload-dropzone--sm { min-height: 140px; padding: 16px; }
.clic-upload-dropzone--lg { min-height: 260px; padding: 40px 16px; }

.clic-upload-dropzone:hover,
.clic-upload-dropzone--dragging {
  background: #fff8f0;
  background: color-mix(in srgb, var(--clic-upload-cor) 6%, transparent);
}

.clic-upload-dropzone--invalid {
  border-color: var(--danger, #dc3545);
}

.clic-upload-dropzone--disabled {
  cursor: not-allowed;
  opacity: .6;
}

.clic-upload-dropzone__icon-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  margin-bottom: 14px;
  border: 1px solid #e5e9f0;
  border-radius: 12px;
  background: #f8f9fb;
  color: var(--clic-upload-cor);
}

.clic-upload-dropzone__title {
  font-weight: 700;
  font-size: 1rem;
  color: #1f2937;
}

.clic-upload-dropzone__subtitle {
  margin-top: 6px;
  font-size: .85rem;
  color: #6b7280;
}

.clic-upload-dropzone__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 16px;
}

.clic-upload-dropzone__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #1f2937;
  font-weight: 600;
  font-size: .875rem;
  line-height: 1.2;
  cursor: pointer;
  transition: border-color .15s ease, color .15s ease;
}

.clic-upload-dropzone__btn:hover:not(:disabled),
.clic-upload-dropzone__btn:focus-visible {
  border-color: var(--clic-upload-cor);
  color: var(--clic-upload-cor);
  outline: none;
}

.clic-upload-dropzone__btn:disabled { cursor: not-allowed; }

.clic-upload-dropzone__hint {
  margin-top: 6px;
  font-size: .75rem;
  color: #6b7280;
}

.clic-upload-lista {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
}

.clic-upload-lista li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  margin-bottom: 4px;
  font-size: .875rem;
}

.clic-upload-lista__nome {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.clic-upload-lista__tamanho { color: #6c757d; font-size: .8rem; }
.clic-upload-lista__remover { font-size: 1.25rem; line-height: 1; }
</style>
